import { useRuntimeVariables } from 'app-kit'
import { useGeckoAuth } from 'features/auth'
import { useDocChatCapabilities } from 'features/knowledgeDocumentViewer/components/advancedSearch/hooks/useDocChatCapabilities'
import { useDocChatMultiCallSetting } from 'features/knowledgeDocumentViewer/components/advancedSearch/hooks/useDocChatMultiCallSetting'
import { useDocChatRecovery } from 'features/knowledgeDocumentViewer/components/advancedSearch/hooks/useDocChatRecovery'
import { useDocChatTimeSeriesSetting } from 'features/knowledgeDocumentViewer/components/advancedSearch/hooks/useDocChatTimeSeriesSetting'
import { useResolvedDocChatModelSettings } from 'features/knowledgeDocumentViewer/components/advancedSearch/hooks/useResolvedDocChatModelSettings'
import { loadAttachments } from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/docChatAttachmentStore'
import { hasDocChatCapability } from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/docChatCapabilities'
import { collectDocChatContextDocumentIds } from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/docChatContextDocumentIds'
import {
  createDocChatTurn,
  createDocChatTurnId,
  docChatTurnRequestQuery,
  documentSearchResultFromActiveTurn,
  documentSearchResultFromActiveTurnResponse,
  getDocChatTurnStatus,
  hasInProgressDocChatTurn,
  isClientDocChatTurnId,
  markDocChatTurnError,
  markDocChatTurnRestoredRunning,
  settleDocChatConversationOnRender,
} from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/docChatConversation'
import { DOC_CHAT_START_PATH } from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/docChatFetcher'
import type { DocChatPendingAttachment } from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/docChatImageAttachments'
import {
  getDocChatStreamConversation,
  isDocChatTurnInFlight,
  startDocChatStream,
  subscribeDocChatStream,
} from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/docChatInFlightStore'
import {
  type DocChatContextDocument,
  DocChatRequestSchema,
  type DocumentSearchResult,
} from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/documentSearchApiSchemas'
import {
  inspectionViewScope,
  readInspectionViewContext,
} from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/inspectionViewContextStore'
import {
  getTrendChartState,
  trendPreviewScope,
  useTrendPreview,
} from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/trendPreviewStore'
import { useKnowledgeDocumentUXContextOptional } from 'features/knowledgeDocumentViewer/contexts/KnowledgeDocumentUXContext'
import { usePipelineConfig } from 'features/knowledgeDocumentViewer/contexts/PipelineConfigContext'
import { useSearchActions } from 'features/knowledgeDocumentViewer/contexts/SearchActionsContext'
import type {
  DocChatConversation,
  DocChatSubmittedQueryEvent,
  SearchHistoryItem,
  SearchStateUpdate,
} from 'features/knowledgeDocumentViewer/types'
import { pendingAgenticConversationKey } from 'features/knowledgeDocumentViewer/utils/agenticConversationIdentity'
import { useGeckoLocale } from 'features/locale/GeckoLocaleProvider'
import { useCallback, useEffect, useMemo, useState } from 'react'

import { useDocChatCmmsSetting } from './useDocChatCmmsSetting'

interface UseDocChatArgs {
  enabled: boolean
  shouldStartStream: boolean
  siteId: string
  tabId?: string
  searchQuery: string
  limit?: number
  docChat?: SearchHistoryItem['searchState']['docChat']
  attachments?: DocChatPendingAttachment[]
  contextDocuments?: DocChatContextDocument[]
}

interface UseDocChatResult {
  rawDocumentSearchData: DocumentSearchResult | undefined
  streamingSearchData: DocumentSearchResult | undefined
  isLoadingSearch: boolean
  searchLatencyMs: number | undefined
  refreshDocumentSearch: () => void
}

export function nextHeldStreamingSearchData({
  activeTurnId,
  hasRawDocumentSearchData,
  held,
  heldTurnId,
  turnDerived,
}: {
  activeTurnId: string | undefined
  hasRawDocumentSearchData: boolean
  held: DocumentSearchResult | undefined
  heldTurnId: string | undefined
  turnDerived: DocumentSearchResult | undefined
}): DocumentSearchResult | undefined {
  if (hasRawDocumentSearchData || heldTurnId !== activeTurnId) {
    return turnDerived
  }
  return turnDerived ?? held
}

export function useDocChat({
  enabled,
  shouldStartStream,
  siteId,
  tabId,
  searchQuery,
  limit = 200,
  docChat,
  attachments,
  contextDocuments,
}: UseDocChatArgs): UseDocChatResult {
  const { authorization } = useGeckoAuth()
  const { POWER_BACKEND_AI_URL } = useRuntimeVariables()
  const { isPreviewMode, selectedPipelineConfigId } = usePipelineConfig()
  const { locale } = useGeckoLocale()
  const openTabs = useKnowledgeDocumentUXContextOptional()?.openTabs
  const { onSearchStateChange: onSearchStateChangeForActiveTab } =
    useSearchActions()
  const onSearchStateChange = useCallback(
    (updates: SearchStateUpdate) => {
      if (tabId) {
        onSearchStateChangeForActiveTab(updates, { tabId })
        return
      }
      onSearchStateChangeForActiveTab(updates)
    },
    [onSearchStateChangeForActiveTab, tabId]
  )
  const conversation =
    docChat?.siteId === siteId ? docChat.conversation : undefined
  const artifactId = docChat?.siteId === siteId ? docChat.artifactId : undefined
  useDocChatRecovery({ conversation, siteId, onSearchStateChange })
  const contextDocumentIds = useMemo(
    () =>
      collectDocChatContextDocumentIds({
        conversation,
        openTabs: openTabs ?? [],
        pipelineConfigId: selectedPipelineConfigId,
      }),
    [conversation, openTabs, selectedPipelineConfigId]
  )
  const sessionKey =
    conversation?.id ??
    conversation?.pendingSessionKey ??
    (tabId ? pendingAgenticConversationKey(tabId) : undefined)
  const {
    isInternal,
    model,
    thinkingLevel,
    permissionsReady,
    loaded: modelSettingsLoaded,
  } = useResolvedDocChatModelSettings(siteId)
  const { data: capabilityManifest, error: capabilitiesError } =
    useDocChatCapabilities(siteId)
  // Turn 1 must not start while the manifest is in flight, or capability-gated
  // request fields would be dropped. A failed fetch settles too: the turn then
  // runs without capability-gated fields rather than stalling forever.
  const capabilitiesSettled =
    capabilityManifest !== undefined || capabilitiesError !== undefined
  const { enabled: multiCallWanted } = useDocChatMultiCallSetting()
  const revisionsEnabled =
    multiCallWanted && hasDocChatCapability(capabilityManifest, 'revisions')
  const artifactsEnabled =
    multiCallWanted && hasDocChatCapability(capabilityManifest, 'artifacts')
  const multiCallEnabled =
    multiCallWanted && capabilityManifest?.multi_call === true
  const { enabled: cmmsWanted } = useDocChatCmmsSetting()
  const cmmsEnabled =
    cmmsWanted && hasDocChatCapability(capabilityManifest, 'cmms')
  const { enabled: timeSeriesWanted } = useDocChatTimeSeriesSetting()
  const timeSeriesEnabled =
    timeSeriesWanted && hasDocChatCapability(capabilityManifest, 'time_series')
  const trendScope = trendPreviewScope(tabId)
  const trendPreview = useTrendPreview(trendScope)
  const turnDerivedSearchData = useMemo(
    () => documentSearchResultFromActiveTurn(conversation),
    [conversation]
  )
  const rawDocumentSearchData = useMemo(
    () => documentSearchResultFromActiveTurnResponse(conversation),
    [conversation]
  )

  const writeConversation = useCallback(
    (nextConversation: DocChatConversation) => {
      onSearchStateChange((currentSearchState) => {
        const currentDocChat = currentSearchState.docChat
        if (currentDocChat?.siteId !== siteId) return {}
        if (currentDocChat.conversation === nextConversation) return {}
        return {
          docChat: {
            ...currentDocChat,
            siteId,
            conversation: nextConversation,
          },
        }
      })
    },
    [onSearchStateChange, siteId]
  )

  useEffect(() => {
    const trimmedQuery = searchQuery.trim()
    if (!enabled || !shouldStartStream || conversation || !trimmedQuery) return
    onSearchStateChange((currentSearchState) => {
      if (currentSearchState.docChat?.siteId === siteId) return {}
      const turnId = createDocChatTurnId()
      return {
        docChat: {
          siteId,
          conversation: {
            activeTurnId: turnId,
            turns: [createDocChatTurn({ turnId, query: trimmedQuery })],
          },
        },
      }
    })
  }, [
    conversation,
    enabled,
    onSearchStateChange,
    searchQuery,
    shouldStartStream,
    siteId,
  ])

  useEffect(() => {
    if (!sessionKey) return
    const inFlight = getDocChatStreamConversation(sessionKey)
    if (docChat?.isHydratedHistory && !inFlight) return
    if (inFlight) writeConversation(inFlight)
    return subscribeDocChatStream(sessionKey, writeConversation)
  }, [docChat?.isHydratedHistory, sessionKey, writeConversation])

  const [heldStreamingSearchData, setHeldStreamingSearchData] = useState<{
    turnId: string | undefined
    data: DocumentSearchResult | undefined
  }>({ turnId: undefined, data: undefined })
  const streamingSearchData = nextHeldStreamingSearchData({
    activeTurnId: conversation?.activeTurnId,
    hasRawDocumentSearchData: rawDocumentSearchData !== undefined,
    held: heldStreamingSearchData.data,
    heldTurnId: heldStreamingSearchData.turnId,
    turnDerived: turnDerivedSearchData,
  })
  if (
    heldStreamingSearchData.turnId !== conversation?.activeTurnId ||
    heldStreamingSearchData.data !== streamingSearchData
  ) {
    setHeldStreamingSearchData({
      turnId: conversation?.activeTurnId,
      data: streamingSearchData,
    })
  }

  useEffect(() => {
    if (!conversation || !permissionsReady || !capabilitiesSettled) return
    const activeTurnId = conversation.activeTurnId
    const activeTurn = conversation.turns.find(
      (turn) => turn.id === activeTurnId
    )
    if (
      activeTurn?.requestStarted &&
      getDocChatTurnStatus(activeTurn) === 'loading' &&
      !activeTurn.restoredRunning &&
      !isDocChatTurnInFlight(conversation, activeTurn.id, tabId)
    ) {
      writeConversation(
        conversation.id
          ? markDocChatTurnRestoredRunning({
              conversation,
              turnId: activeTurn.id,
            })
          : markDocChatTurnError({
              conversation,
              turnId: activeTurn.id,
              siteId,
            })
      )
      return
    }
    const submittedQueryEvent = activeTurn?.events.find(
      (event): event is DocChatSubmittedQueryEvent =>
        event.type === 'submitted_query'
    )
    const submittedAttachmentMeta = submittedQueryEvent?.attachments ?? []
    const turnAttachments = (attachments ?? []).filter((attachment) =>
      submittedAttachmentMeta.some((meta) => meta.id === attachment.id)
    )
    const shouldStart =
      shouldStartStream &&
      enabled &&
      (searchQuery.trim().length > 0 || submittedAttachmentMeta.length > 0) &&
      activeTurn !== undefined &&
      getDocChatTurnStatus(activeTurn) === 'loading' &&
      !activeTurn.restoredRunning &&
      !isDocChatTurnInFlight(conversation, activeTurn.id, tabId)

    let cancelled = false
    if (shouldStart && authorization) {
      const start = (attachmentsForTurn: DocChatPendingAttachment[]) => {
        startDocChatStream({
          authorization,
          conversation,
          locale,
          requestBody: JSON.stringify(
            DocChatRequestSchema.parse({
              query: docChatTurnRequestQuery(activeTurn),
              site_id: siteId,
              limit,
              preview_mode: isPreviewMode,
              locale,
              enable_cmms: cmmsEnabled,
              enable_multi_call: multiCallEnabled,
              ...(multiCallEnabled
                ? {
                    inspection_context: readInspectionViewContext(
                      inspectionViewScope(siteId, tabId)
                    ),
                  }
                : {}),
              ...(timeSeriesEnabled
                ? {
                    enable_time_series: true,
                    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                    time_series_protocol: 2,
                    ...(trendPreview
                      ? { viewer: getTrendChartState(trendScope) }
                      : {}),
                  }
                : {}),
              ...(activeTurn.tagSelections?.length
                ? {
                    tag_selections: activeTurn.tagSelections.map(
                      (selection) => ({
                        identifier: selection.identifier,
                        entry_id: selection.entryId,
                      })
                    ),
                  }
                : {}),
              ...(conversation.id ? { conversation_id: conversation.id } : {}),
              ...(isClientDocChatTurnId(activeTurn.id)
                ? { turn_id: activeTurn.id }
                : {}),
              ...(artifactsEnabled && artifactId
                ? { artifact_id: artifactId }
                : {}),
              ...(artifactsEnabled
                ? { context_document_ids: contextDocumentIds }
                : {}),
              ...(revisionsEnabled && contextDocuments?.length
                ? { context_documents: contextDocuments }
                : {}),
              // Only send the model once capabilities have loaded: before that
              // the resolved value is the GCP Gemini fallback, which the Azure
              // backend would reject. Omitting it lets the backend use its default.
              ...(isInternal && modelSettingsLoaded
                ? { model, thinking_level: thinkingLevel }
                : {}),
              ...(attachmentsForTurn.length
                ? {
                    enable_image_ingestion: true,
                    attachments: attachmentsForTurn.map((attachment) => ({
                      mime_type: attachment.mimeType,
                      data_base64: attachment.dataBase64,
                      filename: attachment.name,
                    })),
                  }
                : {}),
            })
          ),
          siteId,
          tabId,
          url: `${POWER_BACKEND_AI_URL}${DOC_CHAT_START_PATH}`,
        })
      }
      const missingIds = submittedAttachmentMeta
        .filter(
          (meta) =>
            !turnAttachments.some((attachment) => attachment.id === meta.id)
        )
        .map((meta) => meta.id)
      if (missingIds.length === 0) {
        start(turnAttachments)
      } else {
        // A reload lost the staged bytes; the attachment store still has them.
        void loadAttachments(missingIds).then((restored) => {
          if (cancelled) return
          if (isDocChatTurnInFlight(conversation, activeTurn.id, tabId)) return
          const combined = [...turnAttachments, ...restored]
          if (searchQuery.trim().length === 0 && combined.length === 0) {
            // Image-only turn whose bytes are gone: error it out rather than
            // posting an empty request or holding a spinner forever.
            const abandoned = settleDocChatConversationOnRender({
              conversation,
              shouldStartStream: true,
              siteId,
              isTurnInFlight: (turnId) =>
                isDocChatTurnInFlight(conversation, turnId, tabId),
            })
            if (abandoned !== conversation) writeConversation(abandoned)
            return
          }
          start(combined)
        })
      }
    }

    const nextConversation = settleDocChatConversationOnRender({
      conversation,
      shouldStartStream,
      siteId,
      isTurnInFlight: (turnId) =>
        isDocChatTurnInFlight(conversation, turnId, tabId) ||
        (shouldStart && turnId === activeTurnId),
    })
    if (nextConversation !== conversation) writeConversation(nextConversation)
    return () => {
      cancelled = true
    }
  }, [
    artifactId,
    artifactsEnabled,
    attachments,
    authorization,
    capabilitiesSettled,
    contextDocumentIds,
    contextDocuments,
    conversation,
    enabled,
    isInternal,
    isPreviewMode,
    limit,
    locale,
    model,
    modelSettingsLoaded,
    permissionsReady,
    POWER_BACKEND_AI_URL,
    revisionsEnabled,
    timeSeriesEnabled,
    cmmsEnabled,
    multiCallEnabled,
    trendPreview,
    trendScope,
    searchQuery,
    shouldStartStream,
    siteId,
    tabId,
    thinkingLevel,
    writeConversation,
  ])

  const isInProgress = conversation
    ? hasInProgressDocChatTurn(conversation.turns)
    : false

  return {
    rawDocumentSearchData,
    streamingSearchData,
    isLoadingSearch: isInProgress && streamingSearchData === undefined,
    searchLatencyMs: undefined,
    refreshDocumentSearch: () => {},
  }
}
